/**
 * The Age Pension steps a plan adds to its journey (owner, 3 Oct 2026).
 *
 * A plan that makes someone eligible needs a claim: the pension is not paid automatically. A plan
 * that moves an existing pension needs the changes reported. And the order matters for aged care:
 * restructure, then claim, then the means assessment, which is then the SA485 (or no form at all)
 * rather than the SA457.
 *
 * Judged on the journey's latest saved plan against today's pension, the same comparison the
 * Statements of Advice make (features/advice/agePensionClaim.ts).
 */
import type { WorkflowStepConfig } from './dashboardConfig.tsx';
import type { DocContext } from '../../features/forms/actionPlanDocuments.ts';
import { pensionChangeKind, type PensionChangeKind } from '../../features/advice/agePensionClaim.ts';
import { plansOfTypes, planPension, PLAN_TYPE_FOR_WORKFLOW, type SavedPlan } from '../../features/forms/planPosition.ts';

const money = (v: number) => `$${Math.round(v).toLocaleString('en-AU')}`;

export interface JourneyPension {
    kind: PensionChangeKind;
    plan: SavedPlan | null;
    planPension: number;
    currentPension: number;
}

/** What the journey's latest plan does to the Age Pension. */
export function journeyPension(workflowKey: string, ctx: DocContext): JourneyPension {
    const type = PLAN_TYPE_FOR_WORKFLOW[workflowKey];
    const plan = type ? plansOfTypes(ctx.savedStrategies as SavedPlan[], [type])[0] ?? null : null;
    const current = Number(ctx.currentPension) || 0;
    const planned = planPension(plan);
    return { kind: plan ? pensionChangeKind(planned, current) : 'none', plan, planPension: planned, currentPension: current };
}

/** The steps, with the claim or the report the plan calls for put in the right place. */
export function withPensionSteps(steps: WorkflowStepConfig[], workflowKey: string, ctx: DocContext): WorkflowStepConfig[] {
    const jp = journeyPension(workflowKey, ctx);
    if (jp.kind === 'none') return steps;

    if (jp.kind === 'newly_eligible') {
        const before = workflowKey === 'aged_care' ? 'sa457_form' : workflowKey === 'home_care' ? 'sah_means_form' : null;
        const order = before === 'sa457_form'
            ? ' Do this before the means assessment: once you are paid the pension, that needs only the SA485 (or no form if there is no home), not the SA457.'
            : before === 'sah_means_form'
                ? ' Do this before the contribution assessment: once you are paid the pension, no SA456 is needed.'
                : '';
        const claim: WorkflowStepConfig = {
            id: 'claim_age_pension',
            name: 'Claim the Age Pension',
            description: `Your plan qualifies you for about ${money(jp.planPension)} a year of Age Pension, but it is not paid automatically. Claim once the changes in the plan are made, online through myGov or on the SA002 and SA369, which are pre-filled for you.${order}`,
            forms: ['pension_claim'],
            estimatedTime: '~30 min online',
            status: 'pending',
        };
        const i = before ? steps.findIndex((s) => s.id === before) : -1;
        return i >= 0 ? [...steps.slice(0, i), claim, ...steps.slice(i)] : [...steps, claim];
    }

    const text = jp.kind === 'increase'
        ? `Your pension is expected to rise from about ${money(jp.currentPension)} to ${money(jp.planPension)} a year, but only once Services Australia knows. Report each change, such as a sale, a purchase or money moved, within 14 days.`
        : `Your pension is expected to fall from about ${money(jp.currentPension)} to ${money(jp.planPension)} a year. Report each change, such as a sale, a purchase or money moved, within 14 days: anything paid at the old rate after a change has to be repaid.`;
    // A journey that already has a reporting step says it there, rather than twice.
    const existing = steps.findIndex((s) => s.id === 'notify_services_australia' || s.id === 'notify_centrelink');
    if (existing >= 0) {
        const s = steps[existing]!;
        return steps.map((x, k) => (k === existing ? { ...s, description: `${s.description} ${text}`, forms: [...s.forms, 'report_change'] } : x));
    }
    return [...steps, { id: 'report_pension_change', name: 'Tell Services Australia', description: text, forms: ['report_change'], estimatedTime: 'Within 14 days', status: 'pending' }];
}
